import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Avatar,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { CloudUpload, PhotoCamera } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { WebsiteSettings } from '../types';

const WebsiteSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiService.get('/website-settings');
      if (response.success) {
        setSettings(response.data as any);
      }
    } catch (err: any) {
      setError('Failed to load website settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (settings) {
      setSettings({
        ...settings,
        [field]: value,
      });
    }
  };

  const handleAddressChange = (field: string, value: string) => {
    if (settings) {
      setSettings({
        ...settings,
        address: {
          ...settings.address,
          [field]: value,
        } as any,
      });
    }
  };

  const handleSocialMediaChange = (platform: string, value: string) => {
    if (settings) {
      setSettings({
        ...settings,
        socialMedia: {
          ...settings.socialMedia,
          [platform]: value,
        },
      });
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 2MB for website logo)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image file must be smaller than 2MB');
      return;
    }

    try {
      setLogoUploading(true);
      setError('');

      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64String = e.target?.result as string;
        
        const response = await apiService.post('/website-settings/logo', {
          logoBase64: base64String,
        });

        if (response.success) {
          setSettings(response.data as any);
          setSuccess('Website logo updated successfully!');
          // Notify navbar to refresh settings
          window.dispatchEvent(new CustomEvent('websiteSettingsUpdated'));
        } else {
          setError('Failed to update website logo');
        }
        setLogoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
      setLogoUploading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await apiService.put('/website-settings', settings);

      if (response.success) {
        setSettings(response.data as any);
        setSuccess('Website settings updated successfully!');
        // Notify navbar to refresh settings
        window.dispatchEvent(new CustomEvent('websiteSettingsUpdated'));
      } else {
        setError('Failed to update website settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Check if user is admin
  if (user?.userType !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading website settings...
        </Typography>
      </Container>
    );
  }

  if (!settings) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load website settings.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Website Settings
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            
            <TextField
              fullWidth
              label="Site Name"
              value={settings.siteName}
              onChange={(e) => handleInputChange('siteName', e.target.value)}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={settings.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Contact Phone"
              value={settings.contactPhone || ''}
              onChange={(e) => handleInputChange('contactPhone', e.target.value)}
              margin="normal"
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Meta Description"
              value={settings.metaDescription || ''}
              onChange={(e) => handleInputChange('metaDescription', e.target.value)}
              margin="normal"
              helperText="Brief description for search engines (max 160 characters)"
            />
          </Paper>
        </Grid>

        {/* Website Logo */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Website Logo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your main website logo. This will appear in the navigation bar and throughout the site.
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                src={settings.siteLogo}
                sx={{ width: 100, height: 100 }}
              >
                <PhotoCamera sx={{ fontSize: 40 }} />
              </Avatar>
              
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="website-logo-upload"
                  type="file"
                  onChange={handleLogoUpload}
                  disabled={logoUploading}
                />
                <label htmlFor="website-logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={logoUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                    disabled={logoUploading}
                  >
                    {logoUploading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  Recommended: 200x60px, max 2MB
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Brand Colors */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Brand Colors
            </Typography>
            
            <TextField
              fullWidth
              label="Primary Color"
              value={settings.primaryColor}
              onChange={(e) => handleInputChange('primaryColor', e.target.value)}
              margin="normal"
              helperText="Main brand color (e.g., #2e7d32)"
            />
            
            <TextField
              fullWidth
              label="Secondary Color"
              value={settings.secondaryColor}
              onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
              margin="normal"
              helperText="Accent color (e.g., #ff6f00)"
            />
          </Paper>
        </Grid>

        {/* Platform Fees */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Platform Fee Structure
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure the platform fees charged to providers for different customer types.
            </Typography>
            
            <TextField
              fullWidth
              label="Standard Fee Percentage"
              type="number"
              value={settings.platformFees?.standardFeePercentage ?? 5}
              onChange={(e) => handleInputChange('platformFees', {
                ...settings.platformFees,
                standardFeePercentage: parseFloat(e.target.value) || 5,
              })}
              margin="normal"
              inputProps={{ min: 0, max: 50, step: 0.5 }}
              helperText="Marketplace first season (bid-accepted). Default: 5%"
            />
            
            <TextField
              fullWidth
              label="Imported Customer Fee Percentage"
              type="number"
              value={settings.platformFees?.importedCustomerFeePercentage ?? 1}
              onChange={(e) => handleInputChange('platformFees', {
                ...settings.platformFees,
                importedCustomerFeePercentage: parseFloat(e.target.value) || 1,
              })}
              margin="normal"
              inputProps={{ min: 0, max: 50, step: 0.5 }}
              helperText="Onboarded customers. Default: 1%"
            />

            <TextField
              fullWidth
              label="Renewal Fee Percentage"
              type="number"
              value={settings.platformFees?.renewalFeePercentage ?? 1}
              onChange={(e) => handleInputChange('platformFees', {
                ...settings.platformFees,
                renewalFeePercentage: parseFloat(e.target.value) || 1,
              })}
              margin="normal"
              inputProps={{ min: 0, max: 50, step: 0.5 }}
              helperText="After customer accepts renewal with same provider. Default: 1%"
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Standard Fee:</strong> Marketplace customers (first season after bid accept).<br />
                <strong>Imported Fee:</strong> Provider onboarded existing customers.<br />
                <strong>Renewal Fee:</strong> Same provider after seasonal renewal accept.<br />
                Platform fee is a % of service price only (not payment processing). Customers pay processing on top via gross-up.
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        {/* Maintenance Mode */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Maintenance Mode
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.isMaintenanceMode}
                  onChange={(e) => handleInputChange('isMaintenanceMode', e.target.checked)}
                />
              }
              label="Enable Maintenance Mode"
            />
            
            {settings.isMaintenanceMode && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Maintenance Message"
                value={settings.maintenanceMessage || ''}
                onChange={(e) => handleInputChange('maintenanceMessage', e.target.value)}
                margin="normal"
                helperText="Message shown to visitors during maintenance"
              />
            )}
          </Paper>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              size="large"
            >
              {saving ? <CircularProgress size={24} /> : 'Save Settings'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WebsiteSettingsPage;
